// Segmented "audience hubs" — one page per professional background.
// Rendered by /pages/TransitionSegmentPage.js and listed in /pages/TransitionsIndexPage.js
// The role-level SEO pages nested under each hub live in this same data file
// (roles[] array) and are rendered by /pages/TransitionRolePage.js.

export const TRANSITION_SEGMENTS = [
  {
    slug: 'commercial-cabin-crew',
    label: 'Commercial cabin crew',
    tagline: 'You fly already. The operational reality is different.',
    metaTitle: 'From Commercial Cabin Crew to Private Aviation | Gate 33',
    metaDescription:
      'You already fly. Private aviation is not a smaller airline — it is a completely different operational envelope. Here is the gap, and how Gate 33 closes it in 3 live online days.',
    heroLine:
      "Thirteen years of cabin service is not the same as thirteen years of running a cabin alone. Here is why operators don't screen for what you'd expect — and what they screen for instead.",
    strengths: [
      { title: 'Safety-first muscle memory', body: 'You already have the cabin-safety reflex embedded — the part of the job most hospitality profiles need to be taught from scratch.' },
      { title: 'UHNW service exposure', body: 'First and business-class flying gives you a working baseline of premium service pace and discretion.' },
      { title: 'Sequence discipline', body: 'You know how to hold a service standard through cabin pressure changes, turbulence and cabin noise most people never experience.' },
      { title: 'Working English under pressure', body: 'You already brief, upsell and de-escalate in a second language while moving.' },
    ],
    gaps: [
      "Catering ordering: at a carrier you never speak to the catering department. In private, you place the order 48 hours out — for a city with no supplier list waiting.",
      "Provisioning for self-sufficient multi-day rotations without galley uplift between sectors.",
      "Cabin flow as the sole crew member, with no purser above you and no ground team behind you.",
      "FBO / handler coordination and the flight-deck relationship at a private-operator scale.",
      "Translating a large-carrier CV into operator language — the single most common blocker.",
    ],
    twentyEighty:
      "In commercial you own about 60% service and 40% procedure, all inside a support structure. In private aviation you own roughly 20% service and 80% operations — and there is no structure below you. That is the transition, not a slightly smaller airline.",
    testimonial: {
      quote: "What stood out was his understanding of what private aviation operators are actually looking for beyond a standard airline profile.",
      name: 'Matteo C.',
      background: 'Cabin Senior — Etihad Airways',
      initials: 'MC',
    },
    linksToExisting: {
      label: 'Existing airline transition pages',
      note: 'The full airline-specific SEO pages already exist. If you fly for one of the carriers below, start there.',
      hrefBase: '/insights/transition/',
      airlines: [
        { slug: 'emirates', name: 'Emirates' },
        { slug: 'qatar-airways', name: 'Qatar Airways' },
        { slug: 'etihad-airways', name: 'Etihad Airways' },
        { slug: 'singapore-airlines', name: 'Singapore Airlines' },
        { slug: 'cathay-pacific', name: 'Cathay Pacific' },
        { slug: 'lufthansa', name: 'Lufthansa' },
        { slug: 'swiss', name: 'SWISS' },
        { slug: 'air-france', name: 'Air France' },
      ],
    },
    roles: [], // roles are handled by the existing airline SEO pages
  },
  {
    slug: 'yacht-crew',
    hidden: true, // PARKED — awaiting industry-reviewed input from user. Do not index, do not link internally.
    label: 'Yacht interior crew',
    tagline: 'Same clients, same standards, no six-month season.',
    metaTitle: 'From Yacht Interior Crew to Private Aviation | Gate 33',
    metaDescription:
      'You already run a private interior for one principal, alone, for weeks. Aviation is the closest professional environment you have not tried. Here is exactly what still needs to be learned.',
    heroLine:
      "Interior crew already live inside the operating mode private aviation demands. What changes is the vessel — and the CV language operators actually read.",
    strengths: [
      { title: 'Autonomy under a principal, for weeks', body: 'You have already run a UHNW interior with no manager on site and no shift ending at 22:00. Operators read that as trust-you-alone-with-an-owner evidence.' },
      { title: 'Provisioning at real scale', body: 'You already order for people, days ahead, in ports where nothing is set up for you.' },
      { title: 'Multi-day rotation logic', body: 'Aircraft rotations look tiny after a Mediterranean season — the concept transfers instantly.' },
      { title: 'Discretion as a professional standard', body: 'Not a preference. A licence to keep working. Operators know this.' },
    ],
    gaps: [
      'Aircraft galley constraints across type — a Global 7500 galley is not a superyacht pantry.',
      'Catering ordered in cities you have never landed in, for a 55-minute leg.',
      'Cabin safety and the pilot–attendant relationship (very different from a bosun or captain).',
      'Turbulence-aware sequencing on very short legs.',
      'CV translation from yacht language ("stew/2nd stew/HoS") into operator language.',
    ],
    twentyEighty:
      "In a yacht season you own maybe 40% service and 60% interior operations. In private aviation you own 20% service and 80% operations — over hours, not weeks, and with fewer bodies on board. It is the same reflex, compressed and airborne.",
    testimonial: null,
    roles: [
      { slug: 'chief-stewardess', title: 'From Chief Stewardess to Private Aviation Cabin Attendant' },
      { slug: 'yacht-stewardess', title: 'From Yacht Stewardess to Private Aviation Cabin Attendant' },
      { slug: 'sole-stewardess', title: 'From Sole Stew to Private Aviation Cabin Attendant' },
      { slug: 'yacht-interior-crew', title: 'From Yacht Interior Crew to Private Aviation Cabin Attendant' },
      { slug: 'yacht-chef', title: 'From Yacht Chef to Private Aviation' },
    ],
  },
  {
    slug: 'hospitality',
    label: 'Hotels & fine dining',
    tagline: "You have the service instinct. Here's what's missing.",
    metaTitle: 'From Hotels & Fine Dining to Private Aviation | Gate 33',
    metaDescription:
      "Front office, F&B, fine dining and hotel operations already give you 80% of the mindset. Here is the operational aviation gap Gate 33 closes in 3 live online days.",
    heroLine:
      "A five-star hotel or fine-dining floor already teaches most of what private aviation demands from the cabin. What it does not teach is the aircraft.",
    strengths: [
      { title: 'Sole late-shift decision-making', body: 'Front office at 23:40 with no manager on site is functionally the same call structure as a private cabin at 03:00 over the Atlantic.' },
      { title: 'Supplier networks built by hand', body: 'You already source last-minute for guests. That is exactly how private aviation catering runs.' },
      { title: 'Guest preference discipline', body: 'You already track it across return visits. Operators call this "recognition" and treat it as senior.' },
      { title: 'Discretion around the same clientele', body: 'The same people who take your presidential suite are the ones flying private. Same expectations, moved.' },
    ],
    gaps: [
      "Galley-aware menu logic across aircraft type and duration.",
      "Provisioning for a self-sufficient multi-day rotation with no restock stops.",
      'Sequence design on very short legs (a full meal on a 55-min flight is decided on the ground, not improvised in the air).',
      'Cabin safety context and pilot–attendant coordination.',
      'CV translation from hotel/F&B language into operator language.',
    ],
    twentyEighty:
      'In hospitality you own maybe 70% service and 30% operations — with a full back-of-house. In private aviation you own 20% service and 80% operations — with no back-of-house. Same standards, different envelope.',
    testimonial: null,
    roles: [
      { slug: 'front-office-manager', title: 'From Front Office Manager to Private Aviation Cabin Attendant' },
      { slug: 'hotel-receptionist', title: 'From Hotel Receptionist to Private Aviation Cabin Attendant' },
      { slug: 'concierge', title: 'From Concierge to Private Aviation Cabin Attendant' },
      { slug: 'guest-relations-manager', title: 'From Guest Relations Manager to Private Aviation Cabin Attendant' },
      { slug: 'maitre-dhotel', title: "From Maître d'Hôtel to Private Aviation Cabin Attendant" },
      { slug: 'chef-de-rang', title: 'From Chef de Rang to Private Aviation Cabin Attendant' },
      { slug: 'sommelier', title: 'From Sommelier to Private Aviation Cabin Attendant' },
      { slug: 'room-service', title: 'From Room Service / In-Room Dining to Private Aviation Cabin Attendant' },
      { slug: 'housekeeping-supervisor', title: 'From Housekeeping Supervisor to Private Aviation Cabin Attendant' },
    ],
  },
  {
    slug: 'private-household',
    label: 'Private households & villas',
    tagline: "Discretion and anticipation transfer. Operations don't.",
    metaTitle: 'From Private Households & Villas to Private Aviation | Gate 33',
    metaDescription:
      'Butlers, house managers and villa staff already operate the way corporate flight attendants do. Here is the aircraft-specific gap Gate 33 covers live in 3 days.',
    heroLine:
      'You already run an entire private operation for one principal, alone. The aircraft is the smallest change in this transition.',
    strengths: [
      { title: 'End-to-end household ownership', body: 'You already own everything from procurement to cellar to staff scheduling, without a manager to escalate to.' },
      { title: 'Confidentiality inside personal space', body: 'You have already been present for years without ever being remembered as present. Operators call this "invisibility" and pay for it.' },
      { title: 'Sourcing on demand, anywhere', body: 'You have built supplier networks for a principal who does not accept "not available".' },
      { title: 'Standards enforcement without a manual', body: 'You already hold the line without a written playbook. That is exactly the private-cabin condition.' },
    ],
    gaps: [
      'Aircraft galley constraints — the pantry is roughly the size of a wardrobe.',
      'Catering under real operator budgets and 48h ordering windows.',
      'Cabin safety and the flight-deck relationship.',
      'Sequence design on very short legs.',
      "CV translation from household language into operator language.",
    ],
    twentyEighty:
      'In a private household you own perhaps 30% service and 70% household operations. In private aviation you own 20% service and 80% aircraft operations. The ratio is almost identical — the object of the operations changes.',
    testimonial: null,
    roles: [
      { slug: 'butler', title: 'From Butler to Private Aviation Cabin Attendant' },
      { slug: 'house-manager', title: 'From House Manager to Private Aviation Cabin Attendant' },
      { slug: 'villa-manager', title: 'From Villa Manager to Private Aviation Cabin Attendant' },
      { slug: 'private-chef', title: 'From Private Chef to Private Aviation' },
      { slug: 'personal-assistant', title: 'From Personal Assistant to Private Aviation Cabin Attendant' },
      { slug: 'housekeeper-governess', title: 'From Housekeeper / Governess to Private Aviation Cabin Attendant' },
    ],
  },
];

export const getSegmentBySlug = (slug) => TRANSITION_SEGMENTS.find((s) => s.slug === slug) || null;
export const getRoleBySlug = (segmentSlug, roleSlug) => {
  const seg = getSegmentBySlug(segmentSlug);
  return seg ? seg.roles.find((r) => r.slug === roleSlug) || null : null;
};
